/**
 * EXP-2.3 · The bookkeeping behind "drop photos anywhere on this screen".
 *
 * Extracted so it can be tested without a DOM, which is this repo's pattern
 * for anything with a decision in it. The component keeps only the wiring.
 *
 * ⚠️ TWO THINGS BREAK DRAG TARGETS, AND BOTH ARE HERE.
 *
 * 1. `dragleave` fires every time the pointer crosses into a CHILD element.
 *    A boolean flag therefore flickers off the instant the cursor passes over
 *    a thumbnail, the caption box or a button, and the highlight strobes. The
 *    fix is to count enters against leaves, not to track a boolean.
 *
 * 2. A drag carrying TEXT or an element — reordering a thumbnail, dragging a
 *    caption's selected text — is not a file drop. Reacting to it lights up a
 *    target that will refuse the drop, and on `drop` hands `files.length === 0`
 *    to an upload path.
 */

/** A minimal view of `DataTransfer`, so callers can pass the real thing. */
export interface DragPayload {
  types?: readonly string[] | string[];
}

/**
 * Whether this drag is carrying files.
 *
 * The browser reports a file drag with `Files` in `dataTransfer.types`. Text
 * and element drags report `text/plain`, `text/html` and friends.
 */
export function carriesFiles(payload: DragPayload | null | undefined): boolean {
  if (!payload?.types) return false;
  return Array.from(payload.types).includes('Files');
}

export interface DragDepth {
  /** Nesting depth. Zero means the pointer has left the target entirely. */
  depth: number;
  /** Whether the target should render as active. */
  active: boolean;
}

export const NOT_DRAGGING: DragDepth = { depth: 0, active: false };

/** Pointer entered the target or one of its children. */
export function onEnter(state: DragDepth): DragDepth {
  const depth = state.depth + 1;
  return { depth, active: true };
}

/**
 * Pointer left the target or one of its children.
 *
 * ⚠️ Clamped at zero. A stray `dragleave` with no matching `dragenter` — which
 * browsers do emit when a drag starts inside the target — would otherwise push
 * the count negative, and every subsequent enter would fail to reach 1.
 */
export function onLeave(state: DragDepth): DragDepth {
  const depth = Math.max(0, state.depth - 1);
  return { depth, active: depth > 0 };
}

/**
 * The drop happened, or the drag was abandoned.
 *
 * ⚠️ Resets to zero rather than decrementing. After a drop the browser sends
 * no balancing `dragleave`, so decrementing would leave the count stuck above
 * zero and the target highlighted for the rest of the session.
 */
export function onDropOrCancel(): DragDepth {
  return NOT_DRAGGING;
}
